import { useState } from 'react';
import {
  Link,
  Unlink,
  CheckCircle,
  XCircle,
  RefreshCw,
  Key,
  Video,
  Building2,
  MessageSquare,
  Hash,
  Send,
  ExternalLink,
  Mail,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import {
  useFathomStatus,
  useFathomConnect,
  useFathomDisconnect,
  useFathomSync,
} from '@/hooks/useMeetings';
import {
  useLeadMagicStatus,
  useLeadMagicConnect,
  useLeadMagicDisconnect,
} from '@/hooks/useLeadMagic';
import {
  useSlackStatus,
  useSlackConnect,
  useSlackDisconnect,
  useSlackTest,
  useSlackChannels,
  useSlackChangeChannel,
} from '@/hooks/useSlack';
import {
  useGmailStatus,
  useGmailAuthUrl,
  useGmailDisconnect,
} from '@/hooks/useSequences';
import { Button, Card, CardHeader, CardBody, Skeleton, toast } from '@solid/ui';
import { formatRelativeTime } from '@/lib/utils';

export default function IntegrationsSettings() {
  return (
    <div className="space-y-6">
      <GmailIntegrationCard />
      <SlackIntegrationCard />
      <FathomIntegrationCard />
      <LeadMagicIntegrationCard />
    </div>
  );
}

function GmailIntegrationCard() {
  const { data: status, isLoading } = useGmailStatus();
  const authUrlMutation = useGmailAuthUrl();
  const disconnectMutation = useGmailDisconnect();

  const isConnected = status && status.isActive;
  const metadata = status?.metadata;

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <Card>
      <CardHeader
        title="Gmail"
        subtitle="Connect your Gmail account to send outbound sequence emails that appear in your Sent folder."
      />

      <CardBody>
        {isConnected ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg bg-success/5 border border-success/20 p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Connected to Gmail</p>
                  <p className="text-xs text-grey-500">
                    {metadata?.email && <>Sending as {metadata.email}</>}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => disconnectMutation.mutate()}
                loading={disconnectMutation.isPending}
              >
                <Unlink className="w-3.5 h-3.5" />
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-grey-50 border border-grey-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-grey-500" />
                <p className="text-sm font-medium text-secondary">Connect Gmail Account</p>
              </div>
              <p className="text-xs text-grey-500 mb-3">
                Connect your Gmail account via OAuth to send outbound emails. Emails will appear in your Sent folder and replies are automatically tracked.
              </p>
              <Button
                size="sm"
                onClick={() => authUrlMutation.mutate()}
                loading={authUrlMutation.isPending}
              >
                <Link className="w-3.5 h-3.5" />
                Connect Gmail
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SlackIntegrationCard() {
  const { data: status, isLoading } = useSlackStatus();
  const connectMutation = useSlackConnect();
  const disconnectMutation = useSlackDisconnect();
  const testMutation = useSlackTest();
  const changeChannelMutation = useSlackChangeChannel();
  const [botToken, setBotToken] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [connectResult, setConnectResult] = useState<'success' | 'error' | null>(null);
  const [tokenValidated, setTokenValidated] = useState(false);

  const handleTest = async () => {
    try {
      await testMutation.mutateAsync();
      toast.success('Test message sent');
    } catch (err: any) {
      toast.error(`Test failed: ${err.message || 'Could not send test message to Slack.'}`);
    }
  };

  const isConnected = status && status.isActive;
  const metadata = status?.metadata;

  // Fetch channels when we have a validated token (setup) or when already connected
  const { data: channels, isLoading: loadingChannels } = useSlackChannels(
    tokenValidated || !!isConnected,
    tokenValidated && !isConnected ? botToken : undefined,
  );

  const handleValidateToken = async () => {
    if (!botToken.trim()) return;
    setTokenValidated(true);
  };

  const handleConnect = async () => {
    if (!botToken.trim() || !signingSecret.trim() || !selectedChannelId) return;
    try {
      await connectMutation.mutateAsync({ botToken, signingSecret, channelId: selectedChannelId });
      setBotToken('');
      setSigningSecret('');
      setSelectedChannelId('');
      setTokenValidated(false);
      setConnectResult('success');
    } catch {
      setConnectResult('error');
    }
  };

  const handleChangeChannel = async (channelId: string) => {
    await changeChannelMutation.mutateAsync(channelId);
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <Card>
      <CardHeader
        title="Slack"
        subtitle="Get notified about new form submissions in Slack with interactive actions to create deals and assign leads."
      />

      <CardBody>
        {isConnected ? (
          <div className="space-y-4">
            {/* Connected State */}
            <div className="flex items-center justify-between rounded-lg bg-success/5 border border-success/20 p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Connected to Slack</p>
                  <p className="text-xs text-grey-500">
                    {metadata?.botName && <>Bot: {metadata.botName}</>}
                    {metadata?.teamName && <> &middot; Workspace: {metadata.teamName}</>}
                    {metadata?.channelName && <> &middot; #{metadata.channelName}</>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTest}
                  loading={testMutation.isPending}
                >
                  <Send className="w-3.5 h-3.5" />
                  Test
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => disconnectMutation.mutate()}
                  loading={disconnectMutation.isPending}
                >
                  <Unlink className="w-3.5 h-3.5" />
                  Disconnect
                </Button>
              </div>
            </div>

            {/* Channel Picker (change channel while connected) */}
            <div className="rounded-lg bg-grey-50 border border-grey-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Hash className="w-4 h-4 text-grey-500" />
                <p className="text-sm font-medium text-secondary">Notification Channel</p>
              </div>
              {loadingChannels ? (
                <div className="flex items-center gap-2 text-sm text-grey-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading channels...
                </div>
              ) : channels && channels.length > 0 ? (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <select
                      value={metadata?.channelId || ''}
                      onChange={(e) => handleChangeChannel(e.target.value)}
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm border border-grey-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      {channels.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          #{ch.name}{ch.isPrivate ? ' (private)' : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400 pointer-events-none" />
                  </div>
                  {changeChannelMutation.isPending && (
                    <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                  )}
                </div>
              ) : (
                <p className="text-sm text-grey-500">
                  Currently posting to <span className="font-medium">#{metadata?.channelName}</span>
                </p>
              )}
              {changeChannelMutation.isSuccess && (
                <p className="text-xs text-success mt-1.5">Channel updated</p>
              )}
            </div>

            {/* Test result */}
            {testMutation.isSuccess && (
              <div className="flex items-center gap-2 text-sm text-success">
                <CheckCircle className="w-4 h-4" />
                Test message sent to #{metadata?.channelName}
              </div>
            )}
            {testMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-error">
                <XCircle className="w-4 h-4" />
                Failed to send test message. Check bot permissions.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Setup Instructions */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm font-medium text-blue-900 mb-2">Setup Instructions</p>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>
                  <a
                    href="https://api.slack.com/apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                  >
                    Create a Slack App
                    <ExternalLink className="w-3 h-3 inline ml-0.5" />
                  </a>
                </li>
                <li>Add Bot Token Scopes: <code className="bg-blue-100 px-1 rounded">chat:write</code>, <code className="bg-blue-100 px-1 rounded">channels:read</code>, <code className="bg-blue-100 px-1 rounded">groups:read</code>, <code className="bg-blue-100 px-1 rounded">users:read</code>, <code className="bg-blue-100 px-1 rounded">users:read.email</code></li>
                <li>Enable Interactivity &mdash; set URL to your <code className="bg-blue-100 px-1 rounded">/api/v1/integrations/slack/interactions</code> endpoint</li>
                <li>Install app to workspace, then paste credentials below</li>
              </ol>
            </div>

            {/* Credentials + Channel Picker */}
            <div className="rounded-lg bg-grey-50 border border-grey-200 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-grey-500" />
                <p className="text-sm font-medium text-secondary">Slack Credentials</p>
              </div>

              <div>
                <label htmlFor="slack-bot-token" className="block text-xs text-grey-500 mb-1">Bot User OAuth Token</label>
                <input
                  id="slack-bot-token"
                  type="password"
                  placeholder="xoxb-..."
                  value={botToken}
                  onChange={(e) => {
                    setBotToken(e.target.value);
                    setConnectResult(null);
                    setTokenValidated(false);
                    setSelectedChannelId('');
                  }}
                  className="w-full px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="slack-signing-secret" className="block text-xs text-grey-500 mb-1">Signing Secret</label>
                <input
                  id="slack-signing-secret"
                  type="password"
                  placeholder="Signing secret from Basic Information"
                  value={signingSecret}
                  onChange={(e) => {
                    setSigningSecret(e.target.value);
                    setConnectResult(null);
                  }}
                  className="w-full px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Channel selection */}
              <div>
                <label htmlFor="slack-channel" className="block text-xs text-grey-500 mb-1">Notification Channel</label>
                {!tokenValidated ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleValidateToken}
                      disabled={!botToken.trim()}
                    >
                      <Hash className="w-3.5 h-3.5" />
                      Load Channels
                    </Button>
                    <p className="text-xs text-grey-400 mt-1">
                      Enter your bot token above, then click to load available channels.
                    </p>
                  </>
                ) : loadingChannels ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-grey-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading channels...
                  </div>
                ) : channels && channels.length > 0 ? (
                  <div className="relative">
                    <select
                      id="slack-channel"
                      value={selectedChannelId}
                      onChange={(e) => {
                        setSelectedChannelId(e.target.value);
                        setConnectResult(null);
                      }}
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm border border-grey-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="">Select a channel...</option>
                      {channels.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          #{ch.name}{ch.isPrivate ? ' (private)' : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400 pointer-events-none" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-error">
                    <XCircle className="w-4 h-4" />
                    No channels found. Check your bot token and ensure <code className="text-xs bg-grey-100 px-1 rounded">channels:read</code> scope is added.
                  </div>
                )}
              </div>

              <Button
                size="sm"
                onClick={handleConnect}
                loading={connectMutation.isPending}
                disabled={!botToken.trim() || !signingSecret.trim() || !selectedChannelId}
              >
                <Link className="w-3.5 h-3.5" />
                Connect
              </Button>

              {connectResult === 'success' && (
                <div className="flex items-center gap-2 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  Connected successfully
                </div>
              )}
              {connectResult === 'error' && (
                <div className="flex items-center gap-2 text-sm text-error">
                  <XCircle className="w-4 h-4" />
                  Failed to connect. Please check your bot token and try again.
                </div>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function FathomIntegrationCard() {
  const { data: status, isLoading } = useFathomStatus();
  const connectMutation = useFathomConnect();
  const disconnectMutation = useFathomDisconnect();
  const syncMutation = useFathomSync();
  const [apiKey, setApiKey] = useState('');
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  const isConnected = status && status.isActive;

  const handleConnect = async () => {
    try {
      await connectMutation.mutateAsync(apiKey);
      setApiKey('');
      setTestResult('success');
    } catch {
      setTestResult('error');
    }
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <Card>
      <CardHeader
        title="Fathom AI"
        subtitle="Connect your Fathom account to automatically sync meeting recordings, summaries, and action items."
      />

      <CardBody>
        {isConnected ? (
          <div className="space-y-4">
            {/* Connected State */}
            <div className="flex items-center justify-between rounded-lg bg-success/5 border border-success/20 p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                  <Video className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Connected to Fathom</p>
                  <p className="text-xs text-grey-500">
                    {status.metadata?.lastSyncAt ? (
                      <>Last sync: {formatRelativeTime(status.metadata.lastSyncAt)}</>
                    ) : (
                      'Not synced yet'
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => syncMutation.mutate()}
                  loading={syncMutation.isPending}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Sync Now
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => disconnectMutation.mutate()}
                  loading={disconnectMutation.isPending}
                >
                  <Unlink className="w-3.5 h-3.5" />
                  Disconnect
                </Button>
              </div>
            </div>

            {/* Sync result */}
            {syncMutation.isSuccess && (
              <div className="flex items-center gap-2 text-sm text-success">
                <CheckCircle className="w-4 h-4" />
                Synced {syncMutation.data?.synced ?? 0} meetings
              </div>
            )}
            {syncMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-error">
                <XCircle className="w-4 h-4" />
                Sync failed. Please check your API key and try again.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* API Key Input */}
            <div className="rounded-lg bg-grey-50 border border-grey-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Key className="w-4 h-4 text-grey-500" />
                <p className="text-sm font-medium text-secondary">Enter your Fathom API Key</p>
              </div>
              <p className="text-xs text-grey-500 mb-3">
                You can find your API key in your Fathom account settings under Developer &gt; API Keys.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  placeholder="fathom_api_..."
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTestResult(null);
                  }}
                  className="flex-1 px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <Button
                  size="sm"
                  onClick={handleConnect}
                  loading={connectMutation.isPending}
                  disabled={!apiKey.trim()}
                >
                  <Link className="w-3.5 h-3.5" />
                  Connect
                </Button>
              </div>

              {testResult === 'success' && (
                <div className="mt-2 flex items-center gap-2 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  Connected successfully
                </div>
              )}
              {testResult === 'error' && (
                <div className="mt-2 flex items-center gap-2 text-sm text-error">
                  <XCircle className="w-4 h-4" />
                  Failed to connect. Please check your API key.
                </div>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LeadMagicIntegrationCard() {
  const { data: status, isLoading } = useLeadMagicStatus();
  const connectMutation = useLeadMagicConnect();
  const disconnectMutation = useLeadMagicDisconnect();
  const [apiKey, setApiKey] = useState('');
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  const isConnected = status && status.isActive;

  const handleConnect = async () => {
    try {
      await connectMutation.mutateAsync(apiKey);
      setApiKey('');
      setTestResult('success');
    } catch {
      setTestResult('error');
    }
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <Card>
      <CardHeader
        title="LeadMagic"
        subtitle="Connect your LeadMagic account to automatically enrich company data for incoming leads."
      />

      <CardBody>
        {isConnected ? (
          <div className="space-y-4">
            {/* Connected State */}
            <div className="flex items-center justify-between rounded-lg bg-success/5 border border-success/20 p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Connected to LeadMagic</p>
                  <p className="text-xs text-grey-500">
                    Company enrichment is active
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => disconnectMutation.mutate()}
                loading={disconnectMutation.isPending}
              >
                <Unlink className="w-3.5 h-3.5" />
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* API Key Input */}
            <div className="rounded-lg bg-grey-50 border border-grey-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Key className="w-4 h-4 text-grey-500" />
                <p className="text-sm font-medium text-secondary">Enter your LeadMagic API Key</p>
              </div>
              <p className="text-xs text-grey-500 mb-3">
                You can find your API key in your LeadMagic dashboard under Settings &gt; API Keys.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  placeholder="lm_api_..."
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTestResult(null);
                  }}
                  className="flex-1 px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <Button
                  size="sm"
                  onClick={handleConnect}
                  loading={connectMutation.isPending}
                  disabled={!apiKey.trim()}
                >
                  <Link className="w-3.5 h-3.5" />
                  Connect
                </Button>
              </div>

              {testResult === 'success' && (
                <div className="mt-2 flex items-center gap-2 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  Connected successfully
                </div>
              )}
              {testResult === 'error' && (
                <div className="mt-2 flex items-center gap-2 text-sm text-error">
                  <XCircle className="w-4 h-4" />
                  Failed to connect. Please check your API key.
                </div>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
